import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Fragment, useState } from "react";
import classNames from "classnames";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export type CreatorDropdownGroup = {
  id: string;
  label: string;
  items: { id: string; label: string; onSelect: () => void }[];
};

export default function PiCreatorDropdown(props: {
  trigger: React.ReactNode;
  items?: JSX.Element[];
  groups?: CreatorDropdownGroup[];
  label?: string;
  /**
   * "square" is the 36px, 6px-radius control with a quiet 1px border used in sequence
   * nodes (blue only while open). "small" is the original 32px pill.
   */
  style?: "default" | "small" | "square";
  triggerChrome?: "default" | "none";
  disabled?: boolean;
}) {
  const {
    trigger,
    items = [],
    groups,
    label,
    style = "default",
    triggerChrome = "default",
    disabled = false,
  } = { ...props };
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // prevent node canvas from zooming when scrolling in the dropdown
  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handlePointerInteraction = (
    e: React.PointerEvent | React.MouseEvent,
  ) => {
    e.stopPropagation();
  };

  const triggerView =
    triggerChrome === "none" ? (
      trigger
    ) : (
      <>
        <div
          className={
            "flex-1 my-auto h-full font-medium truncate text-creator-text text-[14px] text-interaction"
          }
        >
          {trigger}
        </div>
        <div
          className={classNames({
            "my-auto ml-auto text-creator-text": style !== "square",
            "my-auto ml-auto mr-[4px] flex text-[18px] text-creator-text-sub": style === "square",
            "transition duration-100 ease-in-out": true,
            "transform rotate-180": isOpen,
            "transform rotate-0": !isOpen,
          })}
        >
          <ExpandMoreIcon color={"inherit"} fontSize={style === "square" ? "inherit" : undefined} />
        </div>
      </>
    );

  return (
    <div
      className={"flex flex-col gap-[8px] noDrag nowheel"}
      onWheel={handleWheel}
    >
      {label && (
        <div className={"pi-creator-dropdown-label font-medium text-creator-text text-[14px]"}>
          {label}
        </div>
      )}
      <DropdownMenu.Root onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger
          disabled={disabled}
          className={classNames(
            "flex w-full focus:outline-none",
            {
              "bg-creator-primary-background border-solid items-center border-creator-primary rounded-[14px] border-[2px]":
                triggerChrome === "default" && style !== "square",
              "h-[36px] items-center rounded-[6px] border border-solid border-creator-border bg-creator-surface px-[2px] transition-[border-color,box-shadow] duration-150 hover:border-creator-text-soft data-[state=open]:border-creator-primary data-[state=open]:shadow-[0_0_0_3px_rgba(58,196,255,0.15)]":
                triggerChrome === "default" && style === "square",
              "items-stretch": triggerChrome === "none",
              "h-[32px]  rounded-full px-[4px]": style === "small",
              "h-[68px]  px-[12px] py-[10px] ":
                style === "default" && triggerChrome === "default",
              "opacity-60 cursor-not-allowed": disabled,
            },
          )}
          onPointerDown={handlePointerInteraction}
          onPointerMove={handlePointerInteraction}
          onMouseDown={handlePointerInteraction}
        >
          {triggerView}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            style={{
              width: "var(--radix-dropdown-menu-trigger-width)",
              maxWidth: "var(--radix-dropdown-menu-content-available-width)",
            }}
            className={
              "flex flex-col max-h-[200px] pit-creator-scrollbar overflow-y-auto gap-[10px] py-[10px] rounded-[5px] bg-creator-surface overflow-x-hidden" +
              " border-solid" +
              " border-[1px]" +
              " border-creator-border drop-shadow-md z-[10050]"
            }
            onWheel={handleWheel}
            onPointerDown={handlePointerInteraction}
            onPointerMove={handlePointerInteraction}
            onMouseDown={handlePointerInteraction}
            side="bottom"
            align="start"
            sideOffset={5}
            collisionPadding={8}
          >
            {groups?.filter((group) => group.items.length > 0).map((group, index) => (
              <Fragment key={group.id}>
                {index > 0 && <DropdownMenu.Separator className="h-px shrink-0 bg-creator-border mx-[8px]" />}
                <DropdownMenu.Group aria-label={group.label}>
                  <DropdownMenu.Label className="px-[8px] pb-[4px] text-[12px] font-medium text-creator-text-sub break-words">
                    {group.label}
                  </DropdownMenu.Label>
                  {group.items.map((item) => (
                    <DropdownMenu.Item
                      key={item.id}
                      textValue={item.label}
                      title={item.label}
                      onSelect={item.onSelect}
                      className="cursor-pointer outline-none pl-[16px] pr-[8px] py-[8px] text-[14px] text-creator-text break-words data-[highlighted]:bg-creator-primary-washed"
                    >
                      {item.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Group>
              </Fragment>
            ))}
            {items.map((item, index) => {
              return (
                <DropdownMenu.Item
                  key={index}
                  className={
                    "cursor-pointer focus:outline-none px-[8px] hover:bg-creator-primary-washed"
                  }
                  onWheel={handleWheel}
                >
                  {item}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
